import { Company, GeminiModel, Project, CallTranscriptPart, AIProvider, CostMode, LiveVoiceProvider, TTSProvider } from '../types';
import { SettingsService } from './settingsService';
import { GeminiService, decodeAudioData as decodeGeminiPcm, decodeBase64 } from './geminiService';
import { OpenRouterService } from './openRouterService';
import { LocalLLMService } from './localLLMService';
import { OpenAiRealtimeService } from './openAiRealtimeService';
import { ElevenLabsRealtimeService } from './elevenLabsRealtimeService';
import { VoiceStackService } from './voice/voiceStackService';
import { classifyVoiceTurn, sanitizeSpokenReply, validateSpokenReply, hasUnsafeVoiceReply } from './voice/voiceGuards';

let audioContext: AudioContext | null = null;

export function prepareLiveAudioContext() {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(console.error);
  }
}

type TranscriptRole = 'model' | 'user';
type ConversationTurn = { role: TranscriptRole; text: string };

export class LiveAudioService {
  private openAiRealtime = new OpenAiRealtimeService();
  private elevenLabsRealtime = new ElevenLabsRealtimeService();
  private activeEngine: LiveVoiceProvider = LiveVoiceProvider.ELEVENLABS;
  private speechRecognition: any = null;
  private shouldKeepRecognitionAlive = false;
  private isConnected = false;
  private isMuted = false;
  private isSpeaking = false;
  private isProcessingTurn = false;
  private recognitionRestartTimer: number | null = null;
  private currentPlayback: AudioBufferSourceNode | null = null;
  private monitorStream: MediaStream | null = null;
  private monitorContext: AudioContext | null = null;
  private inputAnalyser: AnalyserNode | null = null;
  private deepgramSocket: WebSocket | null = null;
  private deepgramRecorder: MediaRecorder | null = null;
  private deepgramKeepAliveTimer: number | null = null;
  private meteredSttRecorder: MediaRecorder | null = null;
  private meteredSttChunks: Blob[] = [];
  private meteredSttFrame: number | null = null;
  private meteredSttSpeechStartedAt = 0;
  private meteredSttLastVoiceAt = 0;
  private meteredSttAvailable = false;
  private useDeepgramStt = false;
  private outputLevel = 0;
  private bargeInFrame: number | null = null;
  private abortController: AbortController | null = null;
  private sessionId = 0;
  private transcript: Array<{ role: TranscriptRole; text: string; timestamp: number }> = [];
  private recentModelUtterances: string[] = [];
  private recentUserUtterances: Array<{ text: string; timestamp: number }> = [];
  private ignoreInputUntil = 0;
  private conversationHistory: ConversationTurn[] = [];
  private conversationChain: Promise<void> = Promise.resolve();
  private project: Project | null = null;
  private company: Company | null = null;
  private systemInstruction = '';
  private onStatusChange: ((status: string) => void) | null = null;
  private lastError: string | null = null;
  private diagnosticStep = 'idle';
  private degradedReason = '';

  private normalizeComparableText(text: string) {
    return text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private callerIdentity() {
    const settings = SettingsService.getSettings();
    const isElevenLabsKore = settings.voiceName === 'Kore';
    const isOpenAiMarin = settings.openAiRealtimeVoice === 'marin';
    return (isElevenLabsKore || isOpenAiMarin) ? 'AI-assistente' : 'AI-assistent';
  }

  private isMetaReasoningText(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return true;

    const normalized = trimmed.toLowerCase();
    const metaPatterns = [
      /^\*\*.+\*\*$/,
      /^\*\*[^\n]+\*\*[\s\S]*$/m,
      /\bformulating\b/,
      /\bhighlighting\b/,
      /\backnowledging\b/,
      /\bclarifying\b/,
      /\baddressing the observation\b/,
      /\bi've crafted\b/,
      /\bi'm focusing on\b/,
      /\bi recognize the confusion\b/,
      /\bthe script dictated\b/,
      /\bstrategic approach\b/,
      /\blet's refocus\b/,
      /\bi appreciate the feedback\b/
    ];

    return metaPatterns.some((pattern) => pattern.test(normalized));
  }

  private pushTranscript(role: TranscriptRole, text: string) {
    const cleaned = text.trim();
    if (!cleaned) return;
    if (role === 'model' && this.isMetaReasoningText(cleaned)) {
      console.warn('[Voice] Meta-redenering gefilterd uit live transcript:', cleaned.slice(0, 140));
      return;
    }

    const now = Date.now();
    const previous = this.transcript[this.transcript.length - 1];
    if (previous && previous.role === role && now - previous.timestamp < 2000) {
      previous.text = `${previous.text} ${cleaned}`.trim();
      previous.timestamp = now;
      return;
    }

    this.transcript.push({ role, text: cleaned, timestamp: now });
  }

  getTranscript(): string {
    if (this.activeEngine === LiveVoiceProvider.OPENAI) {
      return this.openAiRealtime.getTranscript();
    }
    if (this.activeEngine === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      return this.elevenLabsRealtime.getTranscript();
    }
    return this.transcript
      .map((entry) => `${entry.role === 'model' ? 'BDAT' : 'Contactpersoon'}: ${entry.text}`)
      .join('\n');
  }

  getTranscriptParts(): CallTranscriptPart[] {
    if (this.activeEngine === LiveVoiceProvider.OPENAI) {
      return this.openAiRealtime.getTranscriptParts();
    }
    if (this.activeEngine === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      return this.elevenLabsRealtime.getTranscriptParts();
    }
    return this.transcript.map((entry) => ({
      speaker: entry.role === 'model' ? 'agent' : 'contact',
      text: entry.text
    }));
  }

  getAudioLevels() {
    if (this.activeEngine === LiveVoiceProvider.OPENAI) {
      return this.openAiRealtime.getAudioLevels();
    }
    if (this.activeEngine === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      return this.elevenLabsRealtime.getAudioLevels();
    }
    return {
      input: this.readInputLevel(),
      output: this.outputLevel,
    };
  }

  getLastError() {
    if (this.activeEngine === LiveVoiceProvider.OPENAI) {
      return this.openAiRealtime.getLastError();
    }
    if (this.activeEngine === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      return this.elevenLabsRealtime.getLastError();
    }
    return this.lastError;
  }

  getDiagnostics() {
    return {
      engine: this.activeEngine,
      step: this.diagnosticStep,
      degraded: false,
      degradedReason: this.degradedReason,
    };
  }

  private setStep(step: string) {
    this.diagnosticStep = step;
  }

  private isCurrentSession(sessionId: number) {
    return this.isConnected && this.sessionId === sessionId && !this.abortController?.signal.aborted;
  }

  private readAnalyserLevel(analyser: AnalyserNode | null) {
    if (!analyser) return 0;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let index = 0; index < data.length; index += 1) {
      const centered = (data[index] - 128) / 128;
      sum += centered * centered;
    }
    return Math.min(1, Math.sqrt(sum / data.length) * 6);
  }

  private readInputLevel() {
    return this.readAnalyserLevel(this.inputAnalyser);
  }

  private async startMicMonitor() {
    this.stopMicMonitor();
    if (!navigator.mediaDevices?.getUserMedia) {
      this.degradedReason = 'Microfoonmonitor niet beschikbaar in deze browser.';
      return;
    }

    const settings = SettingsService.getSettings();
    const audio: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: false,
      autoGainControl: false,
    };
    if (settings.audioInputDeviceId) {
      audio.deviceId = { exact: settings.audioInputDeviceId };
    }

    try {
      this.monitorStream = await navigator.mediaDevices.getUserMedia({ audio });
      const ContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      this.monitorContext = new ContextCtor();
      if (this.monitorContext.state === 'suspended') {
        await this.monitorContext.resume();
      }
      const source = this.monitorContext.createMediaStreamSource(this.monitorStream);
      this.inputAnalyser = this.monitorContext.createAnalyser();
      this.inputAnalyser.fftSize = 512;
      source.connect(this.inputAnalyser);
      this.setStep('mic_ready');
    } catch (error) {
      this.stopMicMonitor();
      const message = error instanceof Error ? error.message : 'Microfoon openen mislukt.';
      this.lastError = `Microfoon niet beschikbaar: ${message}`;
      throw new Error(this.lastError);
    }
  }

  private stopMicMonitor() {
    this.inputAnalyser = null;
    if (this.monitorStream) {
      this.monitorStream.getTracks().forEach((track) => track.stop());
      this.monitorStream = null;
    }
    if (this.monitorContext && this.monitorContext.state !== 'closed') {
      void this.monitorContext.close().catch(() => undefined);
    }
    this.monitorContext = null;
  }

  setMuted(muted: boolean) {
    if (this.activeEngine === LiveVoiceProvider.OPENAI) {
      this.openAiRealtime.setMuted(muted);
      return;
    }
    if (this.activeEngine === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      this.elevenLabsRealtime.setMuted(muted);
      return;
    }
    this.isMuted = muted;
    if (muted) {
      this.stopSpeechRecognition();
      this.stopPlayback();
      return;
    }

    if (this.isConnected && !this.isSpeaking && !this.isProcessingTurn && !this.useDeepgramStt && !this.meteredSttAvailable) {
      this.startSpeechRecognition();
    }
  }

  private rememberModelUtterance(text: string) {
    const normalized = this.normalizeComparableText(text);
    if (!normalized) return;
    this.recentModelUtterances.unshift(normalized);
    this.recentModelUtterances = this.recentModelUtterances.slice(0, 8);
  }

  private textSimilarity(left: string, right: string) {
    const leftWords = new Set(this.normalizeComparableText(left).split(' ').filter((word) => word.length > 2));
    const rightWords = new Set(this.normalizeComparableText(right).split(' ').filter((word) => word.length > 2));
    if (!leftWords.size || !rightWords.size) return 0;

    let overlap = 0;
    leftWords.forEach((word) => {
      if (rightWords.has(word)) overlap += 1;
    });

    return overlap / Math.min(leftWords.size, rightWords.size);
  }

  private shouldIgnoreRecognizedUserText(text: string) {
    const normalized = this.normalizeComparableText(text);
    if (!normalized || normalized.length < 2) return true;
    if (Date.now() < this.ignoreInputUntil) return true;

    const duplicateUser = this.recentUserUtterances.some((entry) =>
      Date.now() - entry.timestamp < 8000 &&
      (entry.text === normalized || this.textSimilarity(entry.text, normalized) > 0.82)
    );
    if (duplicateUser) return true;

    return this.recentModelUtterances.some((candidate) =>
      candidate === normalized ||
      candidate.includes(normalized) ||
      normalized.includes(candidate) ||
      this.textSimilarity(candidate, normalized) > 0.58
    );
  }

  private buildRecognitionHints() {
    const values = new Set<string>();
    const addValue = (value?: string) => {
      const cleaned = (value || '').trim();
      if (cleaned && cleaned.length > 1) {
        values.add(cleaned);
      }
    };

    addValue(this.project?.name);
    addValue(this.company?.name);
    addValue(this.company?.industry);
    addValue(this.company?.contactName);
    addValue(this.company?.location);
    addValue(this.company?.email);
    addValue('BDAT');
    addValue(this.callerIdentity());

    return Array.from(values).slice(0, 12).map((text) => ({ text, boost: 6.0 }));
  }

  private pickRecognitionText(result: any) {
    const alternatives = Array.from({ length: result?.length || 0 }, (_, index) => result[index]?.transcript?.trim?.() || '')
      .filter(Boolean);

    if (!alternatives.length) return '';

    const hints = this.buildRecognitionHints().map((item) => this.normalizeComparableText(item.text));
    let best = alternatives[0];
    let bestScore = -1;

    for (const alternative of alternatives) {
      const normalized = this.normalizeComparableText(alternative);
      let score = 0;
      for (const hint of hints) {
        if (hint && normalized.includes(hint)) score += 4;
      }
      if (normalized.length > 10) score += 1;
      if (score > bestScore) {
        best = alternative;
        bestScore = score;
      }
    }

    return best;
  }

  private startSpeechRecognition() {
    const sessionId = this.sessionId;
    if (this.useDeepgramStt) return;
    if (!this.isCurrentSession(sessionId) || this.isMuted) return;

    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      console.info('[Voice] SpeechRecognition niet beschikbaar; alleen BDAT-spraak wordt gelogd.');
      return;
    }

    if (this.speechRecognition) {
      return;
    }

    if (this.recognitionRestartTimer) {
      window.clearTimeout(this.recognitionRestartTimer);
      this.recognitionRestartTimer = null;
    }

    this.shouldKeepRecognitionAlive = true;
    const recognition = new SpeechRecognitionCtor();
    recognition.lang = 'nl-NL';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 3;

    const hints = this.buildRecognitionHints();
    if ('phrases' in recognition && hints.length) {
      try {
        const PhraseCtor = (window as any).SpeechRecognitionPhrase;
        recognition.phrases = PhraseCtor
          ? hints.map((hint) => new PhraseCtor(hint.text, hint.boost))
          : [];
      } catch (error) {
        console.warn('[Voice] SpeechRecognition hints worden niet ondersteund in deze browser:', error);
      }
    }

    recognition.onresult = (event: any) => {
      if (!this.isCurrentSession(sessionId)) return;
      if (this.isMuted || this.isSpeaking || this.isProcessingTurn) {
        return;
      }
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (!result?.isFinal) continue;
        const text = this.pickRecognitionText(result);
        if (!text || this.shouldIgnoreRecognizedUserText(text)) continue;
        this.enqueueUserTurn(text);
      }
    };

    recognition.onend = () => {
      this.speechRecognition = null;
      if (!this.isCurrentSession(sessionId) || !this.shouldKeepRecognitionAlive || this.isMuted) {
        return;
      }
      this.recognitionRestartTimer = window.setTimeout(() => {
        this.recognitionRestartTimer = null;
        try {
          this.startSpeechRecognition();
        } catch (error) {
          console.warn('[Voice] SpeechRecognition restart mislukt:', error);
        }
      }, 350);
    };

    recognition.onerror = (event: any) => {
      if (!this.isCurrentSession(sessionId)) return;
      if (event?.error && event.error !== 'no-speech' && event.error !== 'aborted' && event.error !== 'phrases-not-supported') {
        console.warn('[Voice] SpeechRecognition fout:', event.error);
      }
    };

    this.speechRecognition = recognition;
    try {
      recognition.start();
      console.log('[Voice] Luistermodus actief');
    } catch (error) {
      console.warn('[Voice] SpeechRecognition start mislukt:', error);
      this.speechRecognition = null;
    }
  }

  private stopSpeechRecognition() {
    this.shouldKeepRecognitionAlive = false;
    if (this.recognitionRestartTimer) {
      window.clearTimeout(this.recognitionRestartTimer);
      this.recognitionRestartTimer = null;
    }
    if (!this.speechRecognition) return;

    try {
      this.speechRecognition.onresult = null;
      this.speechRecognition.onend = null;
      this.speechRecognition.onerror = null;
      this.speechRecognition.stop();
    } catch (error) {
      console.warn('[Voice] SpeechRecognition stop mislukt:', error);
    }

    this.speechRecognition = null;
  }

  private startMeteredDeepgramFallback(sessionId: number) {
    if (!this.monitorStream || !this.isCurrentSession(sessionId) || !('MediaRecorder' in window)) return false;
    if (this.meteredSttFrame) return true;

    this.meteredSttAvailable = true;
    this.setStep('metered_stt');

    const stopCurrentRecording = () => {
      const recorder = this.meteredSttRecorder;
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch (error) {
          console.warn('[Voice] Metered STT stop mislukt:', error);
        }
      }
    };

    const beginRecording = () => {
      if (!this.monitorStream || this.meteredSttRecorder?.state === 'recording') return;
      const mimeType = this.pickMediaRecorderMimeType();
      this.meteredSttChunks = [];
      const recorder = mimeType
        ? new MediaRecorder(this.monitorStream, { mimeType })
        : new MediaRecorder(this.monitorStream);

      recorder.ondataavailable = (event) => {
        if (event.data.size) this.meteredSttChunks.push(event.data);
      };
      recorder.onstop = async () => {
        const chunks = this.meteredSttChunks;
        this.meteredSttChunks = [];
        this.meteredSttRecorder = null;
        if (!this.isCurrentSession(sessionId) || !chunks.length) return;

        try {
          const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
          const result = await VoiceStackService.transcribeDeepgramAudio(blob);
          const transcript = result.transcript?.trim();
          if (transcript && !this.shouldIgnoreRecognizedUserText(transcript)) {
            this.enqueueUserTurn(transcript);
          }
        } catch (error) {
          console.warn('[Voice] Metered Deepgram transcriptie mislukt:', error);
        }
      };

      this.meteredSttRecorder = recorder;
      this.meteredSttSpeechStartedAt = Date.now();
      this.meteredSttLastVoiceAt = Date.now();
      recorder.start(150);
    };

    const tick = () => {
      if (!this.isCurrentSession(sessionId) || !this.meteredSttAvailable) {
        this.meteredSttFrame = null;
        return;
      }

      if (!this.isMuted && !this.isSpeaking && !this.isProcessingTurn && !this.useDeepgramStt) {
        const level = this.readInputLevel();
        const now = Date.now();
        if (level > 0.08) {
          if (!this.meteredSttRecorder) beginRecording();
          this.meteredSttLastVoiceAt = now;
        }

        if (this.meteredSttRecorder?.state === 'recording') {
          const duration = now - this.meteredSttSpeechStartedAt;
          const silence = now - this.meteredSttLastVoiceAt;
          if ((duration > 350 && silence > 520) || duration > 6500) {
            stopCurrentRecording();
          }
        }
      }

      this.meteredSttFrame = requestAnimationFrame(tick);
    };

    this.meteredSttFrame = requestAnimationFrame(tick);
    return true;
  }

  private stopMeteredDeepgramFallback() {
    this.meteredSttAvailable = false;
    if (this.meteredSttFrame) {
      cancelAnimationFrame(this.meteredSttFrame);
      this.meteredSttFrame = null;
    }
    if (this.meteredSttRecorder && this.meteredSttRecorder.state !== 'inactive') {
      try {
        this.meteredSttRecorder.stop();
      } catch {
        // ignore stop errors
      }
    }
    this.meteredSttRecorder = null;
    this.meteredSttChunks = [];
  }

  private pickMediaRecorderMimeType() {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
    ];
    return candidates.find((type) => MediaRecorder.isTypeSupported?.(type)) || '';
  }

  private parseDeepgramMessage(payload: any) {
    if (payload?.type === 'SpeechStarted' && this.isSpeaking) {
      this.stopPlayback();
      return;
    }

    if (payload?.type !== 'Results') return;
    const transcript = payload?.channel?.alternatives?.[0]?.transcript?.trim?.() || '';
    const isFinal = Boolean(payload?.is_final || payload?.speech_final);
    if (!transcript || !isFinal) return;
    if (this.shouldIgnoreRecognizedUserText(transcript)) return;
    this.enqueueUserTurn(transcript);
  }

  private async startDeepgramRecognition(sessionId: number) {
    if (!this.monitorStream || !this.isCurrentSession(sessionId) || !('MediaRecorder' in window)) {
      return false;
    }

    const token = await VoiceStackService.getDeepgramToken();
    if (!token.accessToken || !this.isCurrentSession(sessionId)) return false;

    const params = new URLSearchParams({
      model: 'nova-3',
      language: 'nl',
      smart_format: 'true',
      interim_results: 'true',
      vad_events: 'true',
      endpointing: '350',
      utterance_end_ms: '1000',
    });
    const socket = new WebSocket(`wss://api.deepgram.com/v1/listen?${params.toString()}`, ['bearer', token.accessToken]);
    this.deepgramSocket = socket;

    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error('Deepgram WebSocket timeout.'));
      }, 2500);

      socket.onopen = () => {
        window.clearTimeout(timeout);
        resolve();
      };

      socket.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('Deepgram WebSocket verbinding mislukt.'));
      };
    });

    if (!this.isCurrentSession(sessionId)) {
      socket.close();
      return false;
    }

    socket.onmessage = (event) => {
      if (!this.isCurrentSession(sessionId)) return;
      try {
        this.parseDeepgramMessage(JSON.parse(event.data));
      } catch (error) {
        console.warn('[Voice] Deepgram bericht kon niet worden gelezen:', error);
      }
    };
    socket.onclose = () => {
      if (!this.isCurrentSession(sessionId)) return;
      this.useDeepgramStt = false;
      this.setStep('metered_stt');
      this.startMeteredDeepgramFallback(sessionId);
      this.startSpeechRecognition();
    };

    const mimeType = this.pickMediaRecorderMimeType();
    const recorder = mimeType
      ? new MediaRecorder(this.monitorStream, { mimeType })
      : new MediaRecorder(this.monitorStream);
    recorder.ondataavailable = async (event) => {
      if (!this.isCurrentSession(sessionId) || !event.data.size || socket.readyState !== WebSocket.OPEN) return;
      socket.send(await event.data.arrayBuffer());
    };
    recorder.onerror = (event) => {
      console.warn('[Voice] Deepgram MediaRecorder fout:', event);
      this.lastError = 'Microfoonstream naar Deepgram mislukt.';
    };
    recorder.start(250);

    this.deepgramRecorder = recorder;
    this.deepgramKeepAliveTimer = window.setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'KeepAlive' }));
      }
    }, 8000);
    this.useDeepgramStt = true;
    this.setStep('deepgram_listening');
    return true;
  }

  private stopDeepgramRecognition() {
    this.useDeepgramStt = false;
    if (this.deepgramKeepAliveTimer) {
      window.clearInterval(this.deepgramKeepAliveTimer);
      this.deepgramKeepAliveTimer = null;
    }
    if (this.deepgramRecorder && this.deepgramRecorder.state !== 'inactive') {
      try {
        this.deepgramRecorder.stop();
      } catch {
        // ignore stop errors
      }
    }
    this.deepgramRecorder = null;
    if (this.deepgramSocket) {
      try {
        if (this.deepgramSocket.readyState === WebSocket.OPEN) {
          this.deepgramSocket.send(JSON.stringify({ type: 'CloseStream' }));
        }
        this.deepgramSocket.close();
      } catch {
        // ignore close errors
      }
    }
    this.deepgramSocket = null;
  }

  private stopPlayback() {
    if (this.bargeInFrame) {
      cancelAnimationFrame(this.bargeInFrame);
      this.bargeInFrame = null;
    }
    if (this.currentPlayback) {
      try {
        this.currentPlayback.stop();
        this.currentPlayback.disconnect();
      } catch {
        // ignore stop errors
      }
    }
    this.currentPlayback = null;
    this.isSpeaking = false;
    this.outputLevel = 0;
  }

  private startBargeInMonitor(sessionId: number) {
    let hotFrames = 0;
    const tick = () => {
      if (!this.isCurrentSession(sessionId) || !this.currentPlayback || !this.isSpeaking) {
        this.bargeInFrame = null;
        return;
      }

      hotFrames = this.readInputLevel() > 0.22 ? hotFrames + 1 : 0;
      if (hotFrames >= 8) {
        this.stopPlayback();
        if (!this.isMuted) {
          this.startSpeechRecognition();
        }
        return;
      }

      this.bargeInFrame = requestAnimationFrame(tick);
    };
    this.bargeInFrame = requestAnimationFrame(tick);
  }

  private sanitizeSpokenText(text: string) {
    return sanitizeSpokenReply(text);
  }

  private hasUnsafeRoleDrift(text: string) {
    return hasUnsafeVoiceReply(text);
  }

  private fallbackReply(userText: string, isInitialGreeting: boolean) {
    if (isInitialGreeting) {
      return `Goedendag, u spreekt met ${this.callerIdentity()} namens ${this.project?.name || 'onze opdrachtgever'}. Bel ik gelegen voor een korte kennismaking?`;
    }

    if (/geen interesse|niet geinteresseerd|niet geïnteresseerd/i.test(userText)) {
      return 'Helder, dank voor uw openheid. Als het later toch relevant wordt, denk ik graag mee.';
    }

    return `Helder. ${this.project?.name ? `U spreekt met de AI-assistent van ${this.project.name}.` : 'U spreekt met de AI-assistent.'} Ik bel om kort te kijken of een kennismaking zinvol is.`;
  }

  private buildGenerationPrompt(userText: string | null, isInitialGreeting: boolean) {
    const companyLine = this.company?.name
      ? `Doelbedrijf heet exact: ${this.company.name}. Gebruik die naam exact of noem geen bedrijfsnaam als je twijfelt.`
      : 'Er is geen specifiek doelbedrijf geselecteerd.';

    const history = this.conversationHistory
      .slice(-8)
      .map((turn) => `${turn.role === 'model' ? 'BDAT' : 'Contactpersoon'}: ${turn.text}`)
      .join('\n');

    if (isInitialGreeting) {
      return `
Je opent nu het gesprek.
${companyLine}
Project: ${this.project?.name || 'BDAT'}.
${this.project?.description ? `Interne context, niet letterlijk voorlezen: ${this.project.description}` : ''}
Doel: open natuurlijk, check of het gelegen komt, en bouw daarna pas naar een relevante vervolgstap.
Geef exact één korte openingszin of twee zeer korte zinnen in natuurlijk Nederlands.
Noem geen lange propositie en lees geen briefing of projectomschrijving op.
Geen uitleg, geen kopjes, geen markdown, geen interne redenering.
      `.trim();
    }

    return `
${companyLine}
Project: ${this.project?.name || 'BDAT'}.
${this.project?.description ? `Interne projectcontext, niet letterlijk voorlezen: ${this.project.description}` : ''}
${history ? `Gespreksgeschiedenis:\n${history}` : ''}
Laatste uitspraak van contactpersoon: ${userText}
Gedetecteerde intentie: ${classifyVoiceTurn(userText || '')}

Reageer als BDAT-salesconsultant in natuurlijk Nederlands.
Regels:
- maximaal 2 korte zinnen
- spreek alleen letterlijke gesprekstekst
- geen aannames of onbevestigde feiten
- analyseer eerst stil of dit interesse, bezwaar, tijdgebrek, verkeerde persoon, vraag om uitleg of afwijzing is
- reageer direct op de input van de contactpersoon; ga niet door met een script
- noem hooguit één voordeel tegelijk
- stuur op een concrete vervolgstap als dat logisch is
- bel namens de opdrachtgever, nooit namens de lead
- lees projectomschrijving, strategie of selling points nooit letterlijk op
      `.trim();
  }

  private async generateAgentReply(userText: string | null, isInitialGreeting: boolean, sessionId: number) {
    const settings = SettingsService.getSettings();
    const prompt = this.buildGenerationPrompt(userText, isInitialGreeting);
    const systemInstruction = this.systemInstruction;

    let rawReply = '';

    const repairReplyAcrossProviders = async (repairPrompt: string) => {
      if (settings.provider === AIProvider.OPENROUTER) {
        if (settings.costMode === CostMode.FREE_FIRST) {
          return (await OpenRouterService.generateTextFreeFirst(
            settings.openRouterKey,
            repairPrompt,
            systemInstruction,
            settings.defaultModel || OpenRouterService.AUTO_MODEL_ID
          )).text;
        }

        if (settings.costMode === CostMode.CHEAP_PAID_FALLBACK) {
          try {
            return (await OpenRouterService.generateTextFreeFirst(
              settings.openRouterKey,
              repairPrompt,
              systemInstruction,
              settings.defaultModel || OpenRouterService.AUTO_MODEL_ID
            )).text;
          } catch (error) {
            console.warn('[Voice] Free OpenRouter repair failed, using paid auto fallback.', error);
          }
        }

        return await OpenRouterService.generateText(
          settings.openRouterKey,
          repairPrompt,
          systemInstruction,
          settings.costMode === CostMode.BEST_QUALITY ? (settings.defaultModel || OpenRouterService.PAID_AUTO_MODEL_ID) : OpenRouterService.PAID_AUTO_MODEL_ID
        );
      }

      if (
        settings.provider === AIProvider.OLLAMA ||
        settings.provider === AIProvider.LMSTUDIO ||
        settings.provider === AIProvider.LOCAL
      ) {
        return await LocalLLMService.generateText(
          settings.localUrl,
          repairPrompt,
          systemInstruction,
          settings.defaultModel,
          settings.localApiKey
        );
      }

      const gemini = new GeminiService(settings.geminiKey);
      const repaired = await gemini.generateText(repairPrompt, systemInstruction, GeminiModel.FLASH_3, false, undefined, 0);
      return repaired.text || '';
    };

    if (settings.provider === AIProvider.OPENROUTER) {
      this.setStep('openrouter');
      if (settings.costMode === CostMode.FREE_FIRST || settings.costMode === CostMode.CHEAP_PAID_FALLBACK) {
        rawReply = (await VoiceStackService.generateTurn({
          apiKey: settings.openRouterKey,
          model: settings.defaultModel || OpenRouterService.AUTO_MODEL_ID,
          prompt,
          systemPrompt: systemInstruction,
          signal: this.abortController?.signal,
        })).text;
      } else {
        rawReply = (await VoiceStackService.generateTurn({
          apiKey: settings.openRouterKey,
          model: settings.defaultModel || OpenRouterService.PAID_AUTO_MODEL_ID,
          prompt,
          systemPrompt: systemInstruction,
          signal: this.abortController?.signal,
        })).text;
      }
    } else if (
      settings.provider === AIProvider.OLLAMA ||
      settings.provider === AIProvider.LMSTUDIO ||
      settings.provider === AIProvider.LOCAL
    ) {
      rawReply = await LocalLLMService.generateText(
        settings.localUrl,
        prompt,
        systemInstruction,
        settings.defaultModel,
        settings.localApiKey
      );
    } else {
      const gemini = new GeminiService(settings.geminiKey);
      const result = await gemini.generateText(
        prompt,
        systemInstruction,
        settings.defaultModel || GeminiModel.FLASH_3,
        false,
        undefined,
        0
      );
      rawReply = result.text || '';
    }

    if (!this.isCurrentSession(sessionId)) return '';

    let validation = validateSpokenReply(rawReply);
    let cleaned = validation.text;
    if (!validation.ok && !cleaned) {
      return this.fallbackReply(userText || '', isInitialGreeting);
    }

    if (!validation.ok || this.hasUnsafeRoleDrift(cleaned)) {
      const repairPrompt = `
Herschrijf deze reactie veilig voor een BDAT salescall.
Regels:
- spreek namens ${this.project?.name || 'de opdrachtgever'}
- niet namens de lead
- geen eigenaarschap of functies verzinnen
- maximaal 2 korte zinnen
- alleen spreektekst in het Nederlands

Conceptreactie:
${cleaned}
      `.trim();

      try {
        const repaired = await repairReplyAcrossProviders(repairPrompt);
        if (!this.isCurrentSession(sessionId)) return '';
        validation = validateSpokenReply(repaired);
        cleaned = validation.text;
      } catch (error) {
        console.warn('[Voice] Reply repair mislukt, fallback wordt gebruikt:', error);
      }
    }

    if (!cleaned || this.hasUnsafeRoleDrift(cleaned)) {
      return this.fallbackReply(userText || '', isInitialGreeting);
    }

    return cleaned;
  }

  private async playAudioBuffer(buffer: AudioBuffer, onEnded: () => void, sessionId: number) {
    if (!audioContext || !this.isCurrentSession(sessionId)) return;

    await new Promise<void>((resolve) => {
      const source = audioContext!.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext!.destination);
      source.onended = () => {
        if (this.isCurrentSession(sessionId)) {
          onEnded();
        }
        resolve();
      };
      this.currentPlayback = source;
      this.outputLevel = 0.85;
      this.startBargeInMonitor(sessionId);
      source.start(0);
    });
  }

  private async speakText(text: string, sessionId: number) {
    const settings = SettingsService.getSettings();
    prepareLiveAudioContext();
    if (!audioContext || !this.isCurrentSession(sessionId)) return;
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    if (!this.isCurrentSession(sessionId)) return;

    this.stopPlayback();
    this.isSpeaking = true;
    this.outputLevel = 0.25;

    const finish = () => {
      this.isSpeaking = false;
      this.currentPlayback = null;
      this.outputLevel = 0;
      this.ignoreInputUntil = Date.now() + 450;
    };

    try {
      if (settings.ttsProvider === TTSProvider.ELEVENLABS || settings.liveVoiceProvider === LiveVoiceProvider.ELEVENLABS) {
        this.setStep('voice_stack_tts');
        const result = await VoiceStackService.synthesizeSpeech({
          text,
          provider: 'auto',
          language: 'nl',
          voiceStyle: settings.voiceName || 'M1',
        }, this.abortController?.signal);
        const mp3Buffer = result.audio;
        if (!this.isCurrentSession(sessionId)) return;
        const decoded = await audioContext.decodeAudioData(mp3Buffer.slice(0));
        if (!this.isCurrentSession(sessionId)) return;
        this.setStep('playing');
        await this.playAudioBuffer(decoded, finish, sessionId);
        return;
      }

      if (settings.ttsProvider === TTSProvider.GEMINI && settings.geminiKey) {
        const gemini = new GeminiService(settings.geminiKey);
        const base64Audio = await gemini.generateTTS(text, settings.voiceName || 'Zephyr');
        if (base64Audio) {
          if (!this.isCurrentSession(sessionId)) return;
          const pcmBytes = decodeBase64(base64Audio);
          const audioBuffer = await decodeGeminiPcm(pcmBytes, audioContext, 24000, 1);
          if (!this.isCurrentSession(sessionId)) return;
          await this.playAudioBuffer(audioBuffer, finish, sessionId);
          return;
        }
      }
    } catch (error) {
      this.degradedReason = 'ElevenLabs/Gemini TTS mislukt; browser fallback wordt gebruikt.';
      this.lastError = error instanceof Error ? error.message : 'AI TTS playback mislukt.';
      console.warn('[Voice] AI TTS playback mislukt, browser fallback wordt gebruikt:', error);
    }

    if (!this.isCurrentSession(sessionId)) return;

    await new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'nl-NL';
      utterance.rate = settings.voiceSpeed === 'Slow' ? 0.9 : settings.voiceSpeed === 'Fast' ? 1.1 : 1.0;
      utterance.pitch = settings.voiceMood === 'Cheerful' ? 1.1 : settings.voiceMood === 'Serious' ? 0.95 : 1.0;
      utterance.onstart = () => {
        if (!this.isCurrentSession(sessionId)) {
          window.speechSynthesis.cancel();
          resolve();
          return;
        }
        this.outputLevel = 0.65;
      };
      utterance.onend = () => {
        finish();
        resolve();
      };
      utterance.onerror = () => {
        finish();
        resolve();
      };

      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find((voice) => voice.name.toLowerCase().includes((settings.voiceName || '').toLowerCase()));
      const dutch = voices.find((voice) => voice.lang.toLowerCase().startsWith('nl'));
      if (preferred) utterance.voice = preferred;
      else if (dutch) utterance.voice = dutch;

      window.speechSynthesis.cancel();
      this.setStep('browser_tts_degraded');
      window.speechSynthesis.speak(utterance);
    });
  }

  private async runAssistantTurn(userText: string | null, isInitialGreeting: boolean, sessionId = this.sessionId) {
    if (!this.isCurrentSession(sessionId)) return;
    this.isProcessingTurn = true;

    try {
      let reply = '';
      try {
        reply = await this.generateAgentReply(userText, isInitialGreeting, sessionId);
      } catch (error) {
        if (!this.isCurrentSession(sessionId) || (error instanceof DOMException && error.name === 'AbortError')) return;
        console.warn('[Voice] Brain generatie mislukt, lokale fallback wordt gebruikt:', error);
        reply = this.fallbackReply(userText || '', isInitialGreeting);
      }

      if (!reply || !this.isCurrentSession(sessionId)) return;

      this.pushTranscript('model', reply);
      this.rememberModelUtterance(reply);
      this.conversationHistory.push({ role: 'model', text: reply });
      this.conversationHistory = this.conversationHistory.slice(-12);
      await this.speakText(reply, sessionId);
    } catch (error) {
      if (!this.isCurrentSession(sessionId) || (error instanceof DOMException && error.name === 'AbortError')) return;
      this.lastError = error instanceof Error ? error.message : 'Voice turn mislukt.';
      console.error('[Voice] Assistant turn mislukt:', error);
      this.onStatusChange?.('error');
    } finally {
      if (!this.isCurrentSession(sessionId)) return;
      this.isProcessingTurn = false;
    }
  }

  private enqueueUserTurn(text: string) {
    const sessionId = this.sessionId;
    if (!this.isCurrentSession(sessionId)) return;
    if (this.isSpeaking || this.isProcessingTurn) {
      console.warn('[Voice] Invoer genegeerd tijdens spreken of verwerking:', text);
      return;
    }
    const cleaned = this.sanitizeSpokenText(text);
    if (!cleaned) return;
    const normalized = this.normalizeComparableText(cleaned);
    this.recentUserUtterances.unshift({ text: normalized, timestamp: Date.now() });
    this.recentUserUtterances = this.recentUserUtterances.slice(0, 8);

    this.pushTranscript('user', cleaned);
    this.conversationHistory.push({ role: 'user', text: cleaned });
    this.conversationHistory = this.conversationHistory.slice(-12);

    this.conversationChain = this.conversationChain.then(() => this.runAssistantTurn(cleaned, false, sessionId));
  }

  async connect(project: Project | null, company: Company | null, onStatusChange: (status: string) => void, customInstruction?: string) {
    this.disconnect();
    const sessionId = ++this.sessionId;
    this.abortController = new AbortController();

    const settings = SettingsService.getSettings();
    if (settings.liveVoiceProvider === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      this.activeEngine = LiveVoiceProvider.ELEVENLABS_REALTIME;
      this.project = project;
      this.company = company;
      await this.elevenLabsRealtime.connect(project, company, onStatusChange, {
        agentId: settings.elevenLabsAgentId || '',
      });
      console.log('[Voice] ElevenLabs Realtime WebRTC call-engine actief');
      return;
    }

    if (settings.liveVoiceProvider === LiveVoiceProvider.OPENAI && settings.openAiKey?.trim()) {
      this.activeEngine = LiveVoiceProvider.OPENAI;
      this.project = project;
      this.company = company;
      const baseInstruction = customInstruction || `Je bent ${this.callerIdentity()}, een BDAT sales consultant. Handel namens de opdrachtgever, niet namens de lead.`;
      this.systemInstruction = `${baseInstruction} In corporate calls moet je jezelf voorstellen als ${this.callerIdentity()} en expliciet en duidelijk vermelden namens welke klant of welk project je belt (namens ${this.project?.name || 'onze opdrachtgever'}).`;
      await this.openAiRealtime.connect(project, company, onStatusChange, {
        apiKey: settings.openAiKey.trim(),
        model: settings.openAiRealtimeModel || 'gpt-realtime',
        voice: settings.openAiRealtimeVoice || 'marin',
        transcriptionModel: settings.openAiTranscriptionModel || 'gpt-4o-mini-transcribe',
        instructions: this.systemInstruction,
        audioDeviceId: settings.audioInputDeviceId,
      });
      console.log('[Voice] OpenAI Realtime call-engine actief');
      return;
    }

    this.activeEngine = settings.liveVoiceProvider === LiveVoiceProvider.GEMINI
      ? LiveVoiceProvider.ELEVENLABS
      : settings.liveVoiceProvider || LiveVoiceProvider.ELEVENLABS;
    prepareLiveAudioContext();
    this.project = project;
    this.company = company;
    this.onStatusChange = onStatusChange;
    const baseInstruction = customInstruction || `Je bent ${this.callerIdentity()}, een BDAT sales consultant. Handel namens de opdrachtgever, niet namens de lead.`;
    this.systemInstruction = `${baseInstruction} In corporate calls moet je jezelf voorstellen als ${this.callerIdentity()} en expliciet en duidelijk vermelden namens welke klant of welk project je belt (namens ${this.project?.name || 'onze opdrachtgever'}).`;
    this.transcript = [];
    this.recentModelUtterances = [];
    this.recentUserUtterances = [];
    this.ignoreInputUntil = 0;
    this.conversationHistory = [];
    this.conversationChain = Promise.resolve();
    this.lastError = null;
    this.degradedReason = '';
    this.diagnosticStep = 'preflight';
    this.isConnected = true;
    this.isMuted = false;

    onStatusChange('connecting');

    try {
      await this.startMicMonitor();
      if (!this.isCurrentSession(sessionId)) return;
      const deepgramStatus = await VoiceStackService.checkDeepgram().catch((error) => ({
        ok: false,
        degraded: true,
        message: error instanceof Error ? error.message : 'Deepgram status onbekend.',
      }));
      if (!this.isCurrentSession(sessionId)) return;
      if (!deepgramStatus.ok) {
        this.setStep('browser_stt');
        this.startSpeechRecognition();
      } else {
        const deepgramStarted = await this.startDeepgramRecognition(sessionId).catch((error) => {
          console.warn('[Voice] Deepgram realtime kon niet starten, metered transcriptie wordt gebruikt:', error);
          return false;
        });
        if (!deepgramStarted) {
          const meteredStarted = this.startMeteredDeepgramFallback(sessionId);
          this.setStep(meteredStarted ? 'metered_stt' : 'browser_stt');
          if (!meteredStarted) {
            this.startSpeechRecognition();
          }
        }
      }
      onStatusChange('active');
      await this.runAssistantTurn(null, true, sessionId);
      console.log('[Voice] OpenRouter + ElevenLabs call-engine actief');
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : 'Voice connect mislukt.';
      console.error('[Voice] Connect mislukt:', error);
      this.isConnected = false;
      onStatusChange('error');
    }
  }

  disconnect() {
    this.sessionId += 1;
    this.abortController?.abort();
    this.abortController = null;
    if (this.activeEngine === LiveVoiceProvider.OPENAI) {
      this.openAiRealtime.disconnect();
    }
    if (this.activeEngine === LiveVoiceProvider.ELEVENLABS_REALTIME) {
      this.elevenLabsRealtime.disconnect();
    }
    this.shouldKeepRecognitionAlive = false;
    if (this.recognitionRestartTimer) {
      window.clearTimeout(this.recognitionRestartTimer);
      this.recognitionRestartTimer = null;
    }
    this.stopDeepgramRecognition();
    this.stopMeteredDeepgramFallback();
    this.stopSpeechRecognition();
    this.stopPlayback();
    this.stopMicMonitor();
    window.speechSynthesis.cancel();
    this.isConnected = false;
    this.isSpeaking = false;
    this.isProcessingTurn = false;
    this.project = null;
    this.company = null;
    this.onStatusChange = null;
    this.lastError = null;
    this.degradedReason = '';
    this.diagnosticStep = 'idle';
    this.activeEngine = LiveVoiceProvider.ELEVENLABS;
    this.conversationChain = Promise.resolve();
  }
}
